import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { topics } from "@/mocks/topics";
import PartyCompare from "./components/PartyCompare";
import EventTimeline from "./components/EventTimeline";
import RelatedTopics from "./components/RelatedTopics";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((t) => t.id === id);

  const jsonLd = useMemo(() => {
    if (!topic) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: topic.title,
        description: topic.summary,
        datePublished: topic.date,
        dateModified: topic.date,
        inLanguage: "zh-CN",
        about: topic.tags.join("、"),
        articleSection: topic.category,
        author: { "@type": "Organization", name: "吃瓜观测站" },
        publisher: { "@type": "Organization", name: "吃瓜观测站" },
        mainEntityOfPage: `https://ytrongxiang.cn/topic/${topic.id}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://ytrongxiang.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "大瓜合集",
            item: "https://ytrongxiang.cn/list",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: topic.title,
            item: `https://ytrongxiang.cn/topic/${topic.id}`,
          },
        ],
      },
    ];
  }, [topic]);

  usePageMeta({
    title: topic
      ? `${topic.title} · 多方说法对比 - 吃瓜观测站`
      : "事件未找到 - 吃瓜观测站",
    description: topic
      ? `${topic.summary.slice(0, 80)}… 本文汇总该事件的多方爆料、当事人回应与对质现场，并列对比各方说法。`
      : "该事件暂未归档，请返回合集页浏览其他事件。",
    keywords: topic
      ? `${topic.title},网红大瓜,网红大瓜多方爆料,网红大瓜当事人回应,网红大瓜对质现场,${topic.tags.join(",")}`
      : "网红大瓜",
    canonical: topic
      ? `https://ytrongxiang.cn/topic/${topic.id}`
      : "https://ytrongxiang.cn/list",
    jsonLd,
  });

  if (!topic) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <Navbar />
        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这个事件还没有被归档
          </h1>
          <p className="mt-3 font-label text-sm text-foreground-600">
            可能链接已经失效，或者该事件尚未整理。去看看当前正在发酵的大瓜吧。
          </p>
          <Link
            to="/list"
            className="mt-6 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-fire-line"></i>
            浏览大瓜合集
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const sameCategory = topics.filter((t) => t.category === topic.category && t.id !== topic.id);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <article className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "大瓜合集", to: "/list" },
                { label: topic.category, to: `/list?category=${encodeURIComponent(topic.category)}` },
                { label: topic.title },
              ]}
            />

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-medium text-primary-800">
                {topic.category}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs text-accent-900">
                {topic.status}
              </span>
              {topic.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-300 px-3 py-1 font-label text-xs text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                更新于 {topic.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-primary-500"></i>
                热度 {topic.heat}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-links-line"></i>
                {topic.sourceCount} 方说法
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                约 {topic.readingMinutes} 分钟读完
              </span>
            </div>
          </div>
        </article>

        <div className="w-full bg-background-50">
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
            <div className="h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-80 lg:h-[420px]">
              <img
                src={topic.cover}
                alt={topic.title}
                title={`${topic.title} - 网红大瓜`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-8">
                <section id="summary" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    事件概览
                  </h2>
                  <p className="mt-3 font-label text-sm leading-loose text-foreground-700 md:text-base">
                    {topic.summary}
                  </p>
                </section>

                <div className="mt-10">
                  <PartyCompare parties={topic.parties} />
                </div>

                <div className="mt-12">
                  <EventTimeline items={topic.timeline} />
                </div>

                <section id="followups" className="mt-12 scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    后续进展
                  </h2>
                  <p className="mt-2 font-label text-sm text-foreground-600">
                    旧说法不删除，新说法按时间追加，方便对照同一方前后是否一致。
                  </p>
                  <ul className="mt-6 space-y-4">
                    {topic.followups.map((f) => (
                      <li
                        key={f.title}
                        className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
                      >
                        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                          <i className="ri-refresh-line text-lg"></i>
                        </span>
                        <span>
                          <span className="block font-heading text-sm font-semibold text-foreground-950">
                            {f.title}
                          </span>
                          <span className="mt-1 block font-label text-sm leading-relaxed text-foreground-600">
                            {f.desc}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>

                <p className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 p-5 font-label text-xs leading-relaxed text-foreground-500">
                  说明：本文内容均为公开信息的整理与并列对比，不代表本站对任何一方作出定性，一切以当事人及官方最终说明为准。
                </p>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <nav aria-label="本篇要点" className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-label text-sm font-semibold text-foreground-950">本篇要点</h2>
                    <ul className="mt-4 space-y-1">
                      {[
                        { href: "#summary", label: "事件概览", icon: "ri-file-text-line" },
                        { href: "#parties", label: "多方说法对比", icon: "ri-links-line" },
                        { href: "#timeline", label: "事件时间线", icon: "ri-history-line" },
                        { href: "#followups", label: "后续进展", icon: "ri-refresh-line" },
                      ].map((item) => (
                        <li key={item.href}>
                          <a
                            href={item.href}
                            className="flex items-center gap-2.5 rounded-md px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                          >
                            <i className={`${item.icon} text-base text-foreground-400`}></i>
                            {item.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>

                  <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-label text-sm font-semibold text-foreground-950">事件速览</h2>
                    <dl className="mt-4 space-y-3 font-label text-sm">
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">所属分类</dt>
                        <dd className="text-foreground-900">{topic.category}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">当前状态</dt>
                        <dd className="text-foreground-900">{topic.status}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">相关方</dt>
                        <dd className="text-foreground-900">{topic.sourceCount} 方</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">最近更新</dt>
                        <dd className="text-foreground-900">{topic.date}</dd>
                      </div>
                    </dl>
                  </section>

                  {sameCategory.length > 0 && (
                    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                      <h2 className="font-label text-sm font-semibold text-foreground-950">
                        同类事件
                      </h2>
                      <ul className="mt-3 space-y-2">
                        {sameCategory.map((t) => (
                          <li key={t.id}>
                            <Link
                              to={`/topic/${t.id}`}
                              className="block rounded-md border border-background-200 px-3 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                            >
                              <span className="line-clamp-2">{t.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                    <h2 className="font-heading text-sm font-semibold text-foreground-950">
                      有没有漏掉的说法？
                    </h2>
                    <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">
                      发现新的回应或对质记录，欢迎投稿，我们会核实后补充进「后续进展」。
                    </p>
                    <Link
                      to="/about#contact"
                      className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-send-plane-line"></i>
                      去投稿
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        <RelatedTopics topics={topics} currentId={topic.id} />
      </main>

      <Footer />
    </div>
  );
}